import React, { useState, useEffect } from 'react';
import {
  Grid, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, CircularProgress, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { ACTIONS } from '../../../../shared/constants';
import { getUser } from '../../../../shared/auth-service';
import {
  getallemployeesByAirport,
  getProfilesByApplication,
} from '../logic';

const ManagementEmployeeBulkAssign = ({
  handleClose,
  handleSave,
  airports,
  applications,
}) => {
  const userData = getUser();
  const companyId = userData.countryCode.split('-')[0];

  const [selectedAirport, setSelectedAirport] = useState(userData.airport?.id || 0);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [selectedProfileValue, setSelectedProfileValue] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingEmployees(true);
      const data = await getallemployeesByAirport(selectedAirport, companyId);
      setEmployees(data || []);
      setSelectedEmployees([]);
      setLoadingEmployees(false);
    };
    load();
  }, [selectedAirport]);

  useEffect(() => {
    const load = async () => {
      setSelectedProfileValue(null); // resetea el perfil al cambiar aplicación
      if (selectedApplicationId) {
        const filtered = await getProfilesByApplication(selectedApplicationId, companyId);
        setFilteredProfiles(filtered || []);
      } else {
        setFilteredProfiles([]);
      }
    };
    load();
  }, [selectedApplicationId]);

  const handleAddProfile = (e, profileOverride) => {
    const profile = profileOverride;
    if (!profile) return;
    const application = applications.find(a => a.id === selectedApplicationId);
    if (!application) return;

    const alreadyExists = selectedCodes.some(
      el => el.profileId === profile.id && el.applicationId === application.id
    );
    if (alreadyExists) return;

    const updated = [...selectedCodes, {
      id: `${profile.id}-${application.id}`,
      profileId: profile.id,
      description: profile.description,
      applicationId: application.id,
      applicationName: application.name,
      applicationIds: [application.id]
    }];
    setSelectedCodes(updated);
    setIsDirty(true);
    setExpandedGroups(prev => new Set([...prev, application.name]));
  };

  const onSave = () => {
    if (selectedEmployees.length === 0 || selectedCodes.length === 0) return;
    handleSave({
      employeeIds: selectedEmployees.map(e => e.employeeId),
      profiles: selectedCodes.map(({ profileId, description }) => ({
        Id: profileId,
        Description: description
      }))
    });
  };

  const groups = selectedCodes.reduce((acc, item) => {
    const key = item.applicationName || 'Sin aplicación';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupAddIcon sx={{ color: '#00b0ef' }} />
            <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
              Asignación de <span style={{ color: '#00b0ef' }}>Perfiles</span>
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Selección de empleados */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Empleados
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  disableClearable
                  options={[{ id: 0, code: 'Todas' }, ...airports]}
                  getOptionLabel={(o) => o.code || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={[{ id: 0, code: 'Todas' }, ...airports].find(a => a.id === selectedAirport) || null}
                  onChange={(_, newValue) => { setSelectedAirport(newValue?.id ?? 0); setSelectedEmployees([]); }}
                  renderInput={(params) => <TextField {...params} label="Base" size="small" />}
                  noOptionsText="Sin resultados"
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                {loadingEmployees ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Cargando empleados...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    multiple
                    size="small"
                    options={employees}
                    getOptionLabel={(o) => `${o.employeeId} - ${o.name} ${o.lastName}`}
                    value={selectedEmployees}
                    onChange={(_, newValue) => { setSelectedEmployees(newValue); setIsDirty(true); }}
                    isOptionEqualToValue={(o, v) => o.employeeId === v.employeeId}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.employeeId}
                          label={`${option.employeeId} - ${option.name} ${option.lastName}`}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{ bgcolor: '#e8f0fe', color: '#1a3072' }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Empleados seleccionados (${selectedEmployees.length})`}
                        placeholder={selectedEmployees.length === 0 ? 'Buscar y seleccionar...' : ''}
                      />
                    )}
                    noOptionsText="Sin resultados"
                  />
                )}
              </Grid>
              {selectedEmployees.length === 1 && selectedEmployees[0]?.employeePosition?.name && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      Cargo:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a3072', fontWeight: 500 }}>
                      {selectedEmployees[0].employeePosition.name}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Selección de perfiles */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Asignación de Perfiles
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <Autocomplete
                  size="small"
                  options={applications}
                  getOptionLabel={(o) => o.name || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={applications.find(a => a.id === selectedApplicationId) || null}
                  onChange={(_, newValue) => setSelectedApplicationId(newValue ? newValue.id : '')}
                  renderInput={(params) => <TextField {...params} label="Aplicación" variant="outlined" size="small" />}
                  noOptionsText="Sin resultados"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <Autocomplete
                  size="small"
                  options={filteredProfiles}
                  getOptionLabel={(o) => o.description || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={selectedProfileValue}
                  onChange={(_, newValue) => {
                    setSelectedProfileValue(newValue || null);
                    if (newValue) handleAddProfile(null, newValue);
                  }}
                  renderInput={(params) => <TextField {...params} label="Perfil" variant="outlined" size="small" disabled={!selectedApplicationId} />}
                  noOptionsText="Seleccione una aplicación primero"
                />
              </FormControl>
            </Grid>
          </Grid>

          {selectedCodes.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
              <Typography variant="body2">Sin perfiles seleccionados</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
              {Object.entries(groups).map(([appName, profs]) => (
                <Accordion key={appName} expanded={expandedGroups.has(appName)} disableGutters elevation={0}
                  onChange={(_, isExpanded) => setExpandedGroups(prev => { const next = new Set(prev); isExpanded ? next.add(appName) : next.delete(appName); return next; })}
                  sx={{ border: '1px solid #e0e4ed', borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: '#f0f4ff', borderRadius: '8px', minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          {appName}
                        </Typography>
                        <Chip label={profs.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1a3072', color: '#fff' }} />
                      </Box>
                      <Chip
                        icon={<DeleteSweepIcon sx={{ fontSize: '14px !important' }} />}
                        label="Quitar todo"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const ids = new Set(profs.map(p => p.id));
                          setSelectedCodes(prev => prev.filter(el => !ids.has(el.id)));
                          setIsDirty(true);
                        }}
                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#e53935', border: '1px solid #fecaca', '& .MuiChip-icon': { color: '#e53935' }, '&:hover': { bgcolor: '#fee2e2' } }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profs.map(p => (
                        <Chip
                          key={p.id}
                          label={p.description}
                          size="small"
                          onDelete={() => { setSelectedCodes(prev => prev.filter(el => el.id !== p.id)); setIsDirty(true); }}
                          sx={{ bgcolor: '#e8f0fe', color: '#1a3072', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Resumen */}
          {(selectedEmployees.length > 0 || selectedCodes.length > 0) && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Se asignarán <strong>{selectedCodes.length} perfil(es)</strong> a <strong>{selectedEmployees.length} empleado(s)</strong>.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ m: 1 }}>
          <Button
            color="primary"
            variant="contained"
            onClick={onSave}
            endIcon={<SaveIcon />}
            disabled={selectedEmployees.length === 0 || selectedCodes.length === 0}
          >
            {ACTIONS.SAVE}
          </Button>
          <Box sx={{ m: 1 }}>
            <Button
              endIcon={<CancelIcon />}
              variant="outlined"
              onClick={() => isDirty ? setShowCancelAlert(true) : handleClose()}
            >
              {ACTIONS.CANCEL}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>
            ¿Descartar cambios?
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="outlined" onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}>
            Seguir editando
          </Button>
          <Button variant="contained" onClick={() => { setShowCancelAlert(false); handleClose(); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}>
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementEmployeeBulkAssign;
